import React from 'react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import DownloadIcon from '@mui/icons-material/Download';
import Button from '@mui/material/Button';
import ReactGA from 'react-ga4'
import addWatermark from '../pdf/watermark';
import logo from '../assets/images/finaculator1.png';
import { useState } from "react";
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';



interface MonthlyDetail {
  monthName: string;
  year: number;
  principalPaid: number;
  interestCharged: number;
  totalPayment: number;
  balance: number;
  loanPaidToDate: string;
}

interface YearlyBreakdown {
  year: number;
  principalPaid: number;
  interestCharged: number;
  totalPayment: number;
  balance: number;
  loanPaidToDate: string;
  monthlyDetails: MonthlyDetail[];
}

interface EMIPDFGeneratorProps {
  monthlyEMI: number | null;
  totalInterest: number | null;
  yearlyBreakdown: YearlyBreakdown[];
  amount: string;
  interest: string;
  period: string;
  // isMonthlySelection: boolean;
  isMonths: boolean;
  isCalculated?:boolean,

}

const EMIPDFGenerator: React.FC<EMIPDFGeneratorProps> = ({
  monthlyEMI,
  totalInterest,
  yearlyBreakdown,
  amount,
  interest,
  period,
  isMonths,
  isCalculated,

}) => {
  
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };
  const generatePDF = () => {
    ReactGA.event({ 'category': 'emi PDF ', 'action': 'EMI PDF Button', 'label': 'EMI PDF' })


    if (!isCalculated) {
      setSnackbarMessage("Please calculate the values before downloading the PDF.");
      setSnackbarOpen(true);
      return;
    }
   

    if (!amount || !interest || !period || !yearlyBreakdown.length || !monthlyEMI || !totalInterest || !isCalculated) {
      setSnackbarMessage("Please ensure all fields are filled before downloading the PDF.");
      setSnackbarOpen(true);
      return;
    }
    

    const doc = new jsPDF();
    const titleFontSize = 14;
    const bodyFontSize = 10;

    const watermarkWidth = 100;
    const watermarkHeight = 100;


    const title = 'EMI Calculation Summary';
    const verticalSpacing = 15;

    doc.setFontSize(titleFontSize);
    doc.text(title, 105, verticalSpacing, { align: 'center' });
    const loanPeriodDisplay = () => {
      const totalMonths = parseInt(period);
      const totalYears = parseInt(period);

      const years = totalYears
      const months = totalMonths % 12;    
      if (isMonths) {
        return `${totalMonths} month${totalMonths !== 1 ? 's' : ''}`;
      } else {
        return `${years} year${years !== 1 ? 's' : ''}`;
      }
    };
    
    
    const inputFieldsTable = [
      ['Field', 'Value'],
      ['Loan Amount', `${amount}`],
      ['Interest Rate', `${interest}%`],
      ['Loan Period', loanPeriodDisplay()], 
      ['Monthly EMI', `${monthlyEMI?.toFixed(2)}`],
      ['Total Interest Paid', `${totalInterest?.toFixed(2)}`],
    ];

    autoTable(doc, {
      head: [inputFieldsTable[0]],
      body: inputFieldsTable.slice(1),
      startY: verticalSpacing + 20,
      headStyles: { fillColor: [22, 160, 133] },
      theme: 'grid',
      styles: { fontSize: bodyFontSize },
    });

    yearlyBreakdown.forEach((yearDetail) => {
      const yearTitle = `Breakdown for Year ${yearDetail.year}`;

      doc.setFontSize(titleFontSize);
      doc.text(yearTitle, 14, doc.autoTable.previous.finalY + 10); 

      const tableColumn = ['Year', 'Principal Paid', 'Interest Charged', 'Total Payment', 'Balance', 'Loan Paid To Date'];
      const tableRows = [
        [
          yearDetail.year,
          `${yearDetail.principalPaid.toFixed(2)}`,
          `${yearDetail.interestCharged.toFixed(2)}`,
          `${yearDetail.totalPayment.toFixed(2)}`,
          `${yearDetail.balance.toFixed(2)}`,
          `${yearDetail.loanPaidToDate}%`,
        ],
      ];

      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: doc.autoTable.previous.finalY + 15,
        headStyles: { fillColor: [22, 160, 133] },
        theme: 'grid',
        styles: { overflow: 'linebreak', cellWidth: 'auto', fontSize: bodyFontSize,halign: 'center' },
      });

      const monthlyTableColumn = ['Month', 'Principal Paid', 'Interest Charged', 'Total Payment', 'Balance','Loan Paid To Date'];
      const monthlyTableRows = yearDetail.monthlyDetails.map(month => [
        month.monthName,
        `${month.principalPaid.toFixed(2)}`,
        `${month.interestCharged.toFixed(2)}`,
        `${month.totalPayment.toFixed(2)}`,
        `${month.balance.toFixed(2)}`,
        `${month.loanPaidToDate}`,

      ]);

      autoTable(doc, {
        head: [monthlyTableColumn],
        body: monthlyTableRows,
        startY: doc.autoTable.previous.finalY + 10,
        headStyles: { fillColor: [22, 160, 133] },
        theme: 'grid',
        styles: { overflow: 'linebreak', cellWidth: 'auto', fontSize: bodyFontSize,halign: 'center'  },
      });
    });
    addWatermark(doc, logo, watermarkWidth, watermarkHeight);
    const img = new Image();
    img.src = logo;
    img.onload = () => {
      const now = new Date();
      const padZero = (num: string | number) => ("0" + num).slice(-2);
      const formattedDate = `${now.getFullYear()}-${padZero(now.getMonth() + 1)}-${padZero(now.getDate())}`;
      const formattedTime = `${padZero(now.getHours())}-${padZero(now.getMinutes())}-${padZero(now.getSeconds())}`;
      
      const timestamp = `${formattedDate}_${formattedTime}`;
      const fileName = `finaculator_emi_calculator_${timestamp}.pdf`;
    
      doc.save(fileName);
    };
  };


  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
      <Button
        variant="contained"
        color="success"
        onClick={generatePDF}
        style={{ marginTop: -50 }}
        startIcon={<DownloadIcon />}
      >
        Download PDF
      </Button>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }} 
      >
        <MuiAlert onClose={handleSnackbarClose} severity="warning" sx={{ width: '100%' }}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </div>
  );
};

export default EMIPDFGenerator;
